import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import notificationService from '../../services/notificationService';
import {
  dispatchCaseMessagesReadEvent,
  markCaseMessagesAsRead,
} from '../../lib/caseUnreadMessages';
import { toast } from 'sonner';
import PlatformMessageBanner from '../platform/PlatformMessageBanner';
import { usePlatformMessages } from '../../hooks/usePlatformMessages';

const TopBar = ({ sidebarCollapsed = false, onSidebarToggle }) => {
  const { messages: platformMessages } = usePlatformMessages();
  const navigate = useNavigate();
  const { signOut, userProfile: authUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const themeMenuRef = useRef(null);

  const { t, i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const lng = (i18n.language || 'fr').toLowerCase().slice(0, 2);
    return { fr: 'FR', en: 'EN', es: 'ES', pl: 'PL' }[lng] || 'FR';
  });

  const user = {
    avatar: authUser?.full_name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || 'U',
  };

  // ============================================================================
  // FETCH NOTIFICATIONS
  // ============================================================================

  const fetchNotifications = async () => {
    if (!authUser?.id) return;

    try {
      setLoading(true);
      const data = await notificationService.getUserNotifications(authUser.id);
      setNotifications(data);

      const count = await notificationService.getUnreadCount(authUser.id);
      setUnreadCount(count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // REAL-TIME SUBSCRIPTION
  // ============================================================================

  useEffect(() => {
    if (!authUser?.id) return;

    // Initial fetch
    fetchNotifications();

    // Subscribe to real-time updates
    const subscription = notificationService.subscribeToNotifications(
      authUser.id,
      (newNotification) => {
        setNotifications((prev) => [newNotification, ...prev]);
        setUnreadCount((prev) => prev + 1);

        // Show toast for new notification
        toast.info(newNotification.title, {
          description: newNotification.message,
          action: newNotification.link
            ? {
                label: t('common.view'),
                onClick: () => navigate(newNotification.link),
              }
            : undefined,
        });
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [authUser?.id]);

  useEffect(() => {
    const refreshOnMessagesRead = () => {
      fetchNotifications();
    };
    window.addEventListener('case-messages-read', refreshOnMessagesRead);
    return () => window.removeEventListener('case-messages-read', refreshOnMessagesRead);
  }, [authUser?.id]);

  // ============================================================================
  // CLICK OUTSIDE HANDLERS
  // ============================================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef?.current && !notificationRef?.current?.contains(event?.target)) {
        setShowNotifications(false);
      }
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setShowUserMenu(false);
      }
      if (themeMenuRef?.current && !themeMenuRef?.current?.contains(event?.target)) {
        setShowThemeMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleLanguageToggle = () => {
    const newLang = currentLanguage === 'EN' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
    setCurrentLanguage(newLang.toUpperCase());
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      const caseId =
        notification.metadata?.caseId ||
        notification.metadata?.case_id ||
        notification.case_id;
      const isCaseMessage =
        notification.metadata?.messageType === 'case_message' ||
        /message/i.test(notification.title || '');

      if (caseId && authUser?.id && isCaseMessage) {
        await markCaseMessagesAsRead(caseId, authUser.id);
        await notificationService.markCaseNotificationsAsRead(authUser.id, caseId);
        dispatchCaseMessagesReadEvent(caseId);
        await fetchNotifications();
      } else if (!notification.is_read) {
        await notificationService.markAsRead(notification.id);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, is_read: true } : n
          )
        );
      }

      if (notification.link) {
        const targetLink =
          caseId && isCaseMessage
            ? `/case-detail/${caseId}?tab=messages`
            : notification.link;
        navigate(targetLink);
      }

      setShowNotifications(false);
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead(authUser.id);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success(t('toast.notificationsMarkedRead'));
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error(t('errors.updateFailed'));
    }
  };

  const handleDeleteNotification = async (notificationId, event) => {
    event.stopPropagation();
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      const deleted = notifications.find((n) => n.id === notificationId);
      if (!deleted?.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
      toast.success(t('toast.notificationDeleted'));
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error(t('errors.deleteFailed'));
    }
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'urgent':
        return 'AlertCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'success':
        return 'CheckCircle';
      case 'error':
        return 'XCircle';
      default:
        return 'Info';
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('timeAgo.now');
    if (diffMins < 60) return t('timeAgo.minutes', { count: diffMins });
    if (diffHours < 24) return t('timeAgo.hours', { count: diffHours });
    if (diffDays === 1) return t('timeAgo.yesterday');
    if (diffDays < 7) return t('timeAgo.days', { count: diffDays });
    return past.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-GB');
  };

  useEffect(() => {
    const count = platformMessages?.length ?? 0;
    document.documentElement.classList.toggle('has-platform-banners', count > 0);
    if (count > 0) {
      document.documentElement.style.setProperty('--platform-banner-slots', String(count));
    } else {
      document.documentElement.style.removeProperty('--platform-banner-slots');
    }
    return () => {
      document.documentElement.classList.remove('has-platform-banners');
      document.documentElement.style.removeProperty('--platform-banner-slots');
    };
  }, [platformMessages?.length]);

  // ============================================================================
  // RENDER
  // ============================================================================

  const stackClass = `topbar-stack with-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`;

  return (
    <div className={stackClass}>
    <header className="topbar">
      <div className="topbar-content">
        <div className="topbar-left">
          <button className="topbar-toggle" onClick={onSidebarToggle}>
            <Icon name={sidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
          </button>
        </div>

        <div className="topbar-right">
          {/* NOTIFICATIONS */}
          <div className="topbar-notification" ref={notificationRef}>
            <button onClick={() => setShowNotifications(!showNotifications)}>
              <Icon name="Bell" size={20} />
              {unreadCount > 0 && (
                <span className="topbar-notification-badge">{unreadCount}</span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 top-12 w-96 bg-card border border-border rounded-lg shadow-modal animate-slideDown z-50">
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">
                    {t('topbar.notifications')} {unreadCount > 0 && `(${unreadCount})`}
                  </h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="text-xs text-primary hover:text-primary/80"
                    >
                      {t('topbar.markAllAsRead')}
                    </button>
                  )}
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                      <p className="text-sm text-muted-foreground mt-2">{t('common.loading')}</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">{t('topbar.noNotifications')}</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 border-b border-border hover:bg-muted cursor-pointer transition-colors duration-200 ${
                          !notification.is_read ? 'bg-primary/5' : ''
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`notification-badge ${notification.type}`}>
                            <Icon name={getNotificationIcon(notification.type)} size={14} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p
                                className={`text-sm font-medium text-foreground ${
                                  !notification.is_read ? 'font-semibold' : ''
                                }`}
                              >
                                {notification.title}
                              </p>
                              <button
                                onClick={(e) => handleDeleteNotification(notification.id, e)}
                                className="p-1 hover:bg-destructive/10 rounded transition-colors"
                                title={t('common.delete')}
                              >
                                <Icon name="X" size={14} className="text-muted-foreground" />
                              </button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {getTimeAgo(notification.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {notifications.length > 0 && (
                  <div className="p-3 text-center border-t border-border">
                    <button
                      onClick={() => {
                        navigate('/notifications');
                        setShowNotifications(false);
                      }}
                      className="text-sm text-primary hover:text-primary/80 font-medium"
                    >
                      {t('topbar.seeAllNotifications')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SÉLECTEUR DE THÈME (clair / sombre / neutre glass) */}
          <div className="topbar-language relative" ref={themeMenuRef}>
            <button
              type="button"
              onClick={() => setShowThemeMenu((v) => !v)}
              className="p-2 rounded-lg hover:bg-muted transition-colors duration-200 cursor-pointer flex items-center gap-1"
              title={t('topbar.themeMenuTitle')}
              aria-label={t('topbar.themeMenuAria')}
              aria-expanded={showThemeMenu}
              aria-haspopup="true"
            >
              {theme === 'dark' ? (
                <Icon name="Moon" size={20} className="text-foreground" />
              ) : theme === 'neutral' ? (
                <Icon name="Sparkles" size={20} className="text-foreground" />
              ) : (
                <Icon name="Sun" size={20} className="text-foreground" />
              )}
              <Icon name="ChevronDown" size={14} className="text-muted-foreground hidden sm:block" />
            </button>
            {showThemeMenu && (
              <div
                className="absolute right-0 top-12 w-56 bg-card border border-border rounded-lg shadow-modal animate-slideDown z-50 py-1"
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setTheme('light');
                    setShowThemeMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground hover:bg-muted text-left"
                >
                  <Icon name="Sun" size={18} className="text-muted-foreground shrink-0" />
                  <span className="flex-1">{t('topbar.themeLight')}</span>
                  {theme === 'light' && <Icon name="Check" size={16} className="text-primary shrink-0" />}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setTheme('neutral');
                    setShowThemeMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground hover:bg-muted text-left"
                >
                  <Icon name="Sparkles" size={18} className="text-muted-foreground shrink-0" />
                  <span className="flex-1">{t('topbar.themeNeutral')}</span>
                  {theme === 'neutral' && <Icon name="Check" size={16} className="text-primary shrink-0" />}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setTheme('dark');
                    setShowThemeMenu(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground hover:bg-muted text-left"
                >
                  <Icon name="Moon" size={18} className="text-muted-foreground shrink-0" />
                  <span className="flex-1">{t('topbar.themeDark')}</span>
                  {theme === 'dark' && <Icon name="Check" size={16} className="text-primary shrink-0" />}
                </button>
              </div>
            )}
          </div>

          {/* LANGUAGE TOGGLE */}
      <div className="topbar-language">
  <Icon name="Globe" size={20} />
  <select
    value={currentLanguage}
    onChange={(e) => {
      const newLang = e.target.value.toLowerCase();
      i18n.changeLanguage(newLang);
      setCurrentLanguage(e.target.value);
    }}
    className="text-sm font-medium bg-transparent border-none outline-none cursor-pointer"
  >
    <option value="FR">FR</option>
    <option value="EN">EN</option>
    <option value="ES">ES</option>
    <option value="PL">PL</option>
  </select>
</div>

          {/* USER MENU */}
          <div className="topbar-user" ref={userMenuRef}>
            <button onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className="topbar-user-info">
                <div className="topbar-user-name">{authUser?.full_name}</div>
                <div className="topbar-user-role">{authUser?.role}</div>
              </div>
              <Icon name="ChevronDown" size={16} />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-12 w-64 bg-card border border-border rounded-lg shadow-modal animate-slideDown z-50">
                <div className="p-4 border-b border-border">
                  <div className="topbar-user-avatar">{user?.avatar}</div>
                  <p className="text-sm font-medium text-foreground">{authUser?.full_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{authUser?.email}</p>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => navigate('/change-password')}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-lg transition-colors duration-200"
                  >
                    <Icon name="Settings" size={18} />
                    <span>{t('topbar.settings')}</span>
                  </button>
                </div>
                <div className="p-2 border-t border-border">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm text-error hover:bg-muted rounded-lg transition-colors duration-200"
                  >
                    <Icon name="LogOut" size={18} />
                    <span>{t('logout')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
    <PlatformMessageBanner messages={platformMessages} />
    </div>
  );
};

export default TopBar;